/**
 * ReferralDashboard.jsx — Jun 2026
 *
 * Admin BI surface for the referral program. Focus: paid conversions (esp.
 * Growth ₹249) with revenue attribution + a top-referrers leaderboard.
 * All data from `/api/admin/referrals/*` (admin-only). Lazy-loaded.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Users, UserPlus, CircleDollarSign, TrendingUp, Trophy, Gift,
  Crown, Search, Loader2, Coins, Sparkles, ArrowUpRight, Award,
} from 'lucide-react';

const INR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const PCT = (n) => `${Number(n || 0).toFixed(1)}%`;

const TONE = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-600', ring: 'ring-emerald-100' },
  green:   { bg: 'bg-green-50',   text: 'text-green-700',   icon: 'text-green-600',   ring: 'ring-green-100' },
  blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    icon: 'text-blue-600',    ring: 'ring-blue-100' },
  violet:  { bg: 'bg-violet-50',  text: 'text-violet-700',  icon: 'text-violet-600',  ring: 'ring-violet-100' },
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   icon: 'text-amber-600',   ring: 'ring-amber-100' },
  slate:   { bg: 'bg-slate-100',  text: 'text-slate-700',   icon: 'text-slate-600',   ring: 'ring-slate-100' },
};

const STATUS_PILL = {
  growth:  { label: 'Growth ₹249', cls: 'bg-emerald-100 text-emerald-700' },
  paid:    { label: 'Paid',        cls: 'bg-blue-100 text-blue-700' },
  active:  { label: 'Active 7d',   cls: 'bg-amber-100 text-amber-700' },
  pending: { label: 'Pending',     cls: 'bg-slate-100 text-slate-600' },
};

function Kpi({ icon: Icon, label, value, sub, tone = 'slate', testid }) {
  const t = TONE[tone] || TONE.slate;
  return (
    <Card className={`p-4 rounded-2xl border-0 ring-1 ${t.ring} ${t.bg} transition-transform hover:-translate-y-0.5`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
          <p className={`mt-1 text-2xl font-heading font-bold ${t.text}`}>{value}</p>
          {sub ? <p className="mt-1 text-xs text-slate-500">{sub}</p> : null}
        </div>
        <div className={`p-2 rounded-xl bg-white/70`}>
          <Icon className={`w-5 h-5 ${t.icon}`} />
        </div>
      </div>
    </Card>
  );
}

function TrendChart({ data }) {
  if (!data || !data.length) return null;
  const w = 720, h = 140, pad = 24;
  const maxV = Math.max(1, ...data.map((d) => Math.max(d.signups, d.conversions)));
  const bw = (w - pad * 2) / data.length;
  return (
    <div className="overflow-x-auto">
      <svg width={w} height={h} className="min-w-full" data-testid="referral-trend-svg">
        {data.map((d, i) => {
          const x = pad + i * bw;
          const sh = (d.signups / maxV) * (h - pad * 2);
          const ch = (d.conversions / maxV) * (h - pad * 2);
          return (
            <g key={d.date}>
              <rect x={x + bw * 0.15} y={h - pad - sh} width={bw * 0.32} height={sh} rx={2} className="fill-blue-300" />
              <rect x={x + bw * 0.52} y={h - pad - ch} width={bw * 0.32} height={ch} rx={2} className="fill-emerald-500" />
            </g>
          );
        })}
        <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} className="stroke-slate-200" />
      </svg>
      <div className="flex gap-4 text-xs text-slate-500 mt-1">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-300 inline-block" /> Signups</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500 inline-block" /> Conversions</span>
      </div>
    </div>
  );
}

export default function ReferralDashboard() {
  const [overview, setOverview] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  const [convStatus, setConvStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [conv, setConv] = useState(null);
  const [convLoading, setConvLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      apiGet('/admin/referrals/overview?trend_days=30').catch(() => null),
      apiGet('/admin/referrals/leaderboard?limit=20').catch(() => null),
    ]).then(([ov, lb]) => {
      if (!alive) return;
      setOverview(ov);
      setLeaderboard((lb && lb.leaderboard) || []);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const loadConversions = useCallback(async () => {
    setConvLoading(true);
    try {
      const params = new URLSearchParams({
        status: convStatus, search: debounced, page: String(page), page_size: '25',
      });
      const res = await apiGet(`/admin/referrals/conversions?${params.toString()}`);
      setConv(res);
    } catch (e) {
      toast.error('Could not load referral conversions');
    } finally {
      setConvLoading(false);
    }
  }, [convStatus, debounced, page]);

  useEffect(() => { loadConversions(); }, [loadConversions]);
  useEffect(() => { setPage(1); }, [convStatus, debounced]);

  const totalPages = useMemo(
    () => (conv ? Math.max(1, Math.ceil(conv.total / conv.page_size)) : 1),
    [conv]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="referral-loading">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  const o = overview || {};

  return (
    <div className="space-y-8" data-testid="referral-dashboard">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600">
          <Gift className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-heading font-bold text-slate-800">Referral Dashboard</h2>
          <p className="text-sm text-slate-500">Who referred whom, Growth-plan conversions, and revenue earned from referrals.</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <Kpi icon={Users} label="Total Referrals" value={o.total_referrals ?? 0}
             sub={`${o.qualified_active ?? 0} active · ${o.pending ?? 0} pending`} tone="blue" testid="kpi-total-referrals" />
        <Kpi icon={CircleDollarSign} label="Paid Conversions" value={o.paid_conversions ?? 0}
             sub={`${PCT(o.conversion_rate)} conversion rate`} tone="violet" testid="kpi-paid-conversions" />
        <Kpi icon={Sparkles} label="Growth ₹249 Conversions" value={o.growth_conversions ?? 0}
             sub={`${PCT(o.growth_conversion_rate)} of referrals`} tone="emerald" testid="kpi-growth-conversions" />
        <Kpi icon={TrendingUp} label="Revenue Attributed" value={INR(o.attributed_revenue)}
             sub={`${INR(o.growth_attributed_revenue)} from Growth`} tone="green" testid="kpi-attributed-revenue" />
        <Kpi icon={ArrowUpRight} label="Avg / Conversion" value={INR(o.avg_revenue_per_conversion)} tone="slate" testid="kpi-avg-conversion" />
        <Kpi icon={Coins} label="Points Awarded" value={o.points_awarded_total ?? 0}
             sub={`${o.points_redeemed_total ?? 0} redeemed`} tone="amber" testid="kpi-points-awarded" />
        <Kpi icon={Gift} label="Free Months Given" value={o.free_months_given ?? 0} tone="slate" testid="kpi-free-months" />
      </div>

      {/* Trend */}
      <Card className="p-5 rounded-2xl border-0 ring-1 ring-slate-100" data-testid="referral-trend-card">
        <h3 className="text-sm font-heading font-semibold text-slate-700 mb-3">Signups vs Conversions — last 30 days</h3>
        <TrendChart data={o.trend_daily} />
      </Card>

      {/* Leaderboard */}
      <Card className="p-5 rounded-2xl border-0 ring-1 ring-slate-100" data-testid="referral-leaderboard-card">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-amber-500" />
          <h3 className="text-sm font-heading font-semibold text-slate-700">Top Referrers</h3>
        </div>
        {leaderboard.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="leaderboard-empty">No referrals yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-slate-100">
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">Referrer</th>
                  <th className="py-2 pr-3 text-center">Referrals</th>
                  <th className="py-2 pr-3 text-center">Paid</th>
                  <th className="py-2 pr-3 text-center">Growth</th>
                  <th className="py-2 pr-3 text-center">Points</th>
                  <th className="py-2 pr-3 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((r, i) => (
                  <tr key={r.user_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`leaderboard-row-${i}`}>
                    <td className="py-2.5 pr-3">
                      {i === 0 ? <Crown className="w-4 h-4 text-amber-500" /> :
                       i < 3 ? <Award className="w-4 h-4 text-slate-400" /> :
                       <span className="text-slate-400">{i + 1}</span>}
                    </td>
                    <td className="py-2.5 pr-3">
                      <div className="font-medium text-slate-700">{r.name}</div>
                      <div className="text-xs text-slate-400">{r.email}</div>
                    </td>
                    <td className="py-2.5 pr-3 text-center text-slate-600">{r.total_referrals}</td>
                    <td className="py-2.5 pr-3 text-center text-blue-600 font-medium">{r.paid_conversions}</td>
                    <td className="py-2.5 pr-3 text-center text-emerald-600 font-medium">{r.growth_conversions}</td>
                    <td className="py-2.5 pr-3 text-center text-amber-600">{r.points}</td>
                    <td className="py-2.5 pr-3 text-right font-semibold text-slate-800">{INR(r.attributed_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Conversions ledger */}
      <Card className="p-5 rounded-2xl border-0 ring-1 ring-slate-100" data-testid="referral-conversions-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-sm font-heading font-semibold text-slate-700">Referral Ledger</h3>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="referral-search"
                placeholder="Search name / email / plan"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9 w-56 rounded-xl"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {[
            ['all', 'All'], ['growth', 'Growth ₹249'], ['paid', 'All Paid'],
            ['active', 'Active'], ['pending', 'Pending'],
          ].map(([val, label]) => (
            <button
              key={val}
              data-testid={`referral-filter-${val}`}
              onClick={() => setConvStatus(val)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                convStatus === val ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {convLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-emerald-500 animate-spin" /></div>
        ) : !conv || conv.items.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="conversions-empty">No matching referrals.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-slate-100">
                    <th className="py-2 pr-3">Referrer</th>
                    <th className="py-2 pr-3">Referred User</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Plan</th>
                    <th className="py-2 pr-3 text-right">Amount</th>
                    <th className="py-2 pr-3 text-center">Points</th>
                  </tr>
                </thead>
                <tbody>
                  {conv.items.map((r, i) => {
                    const pill = STATUS_PILL[r.status] || STATUS_PILL.pending;
                    return (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`conversion-row-${i}`}>
                        <td className="py-2.5 pr-3">
                          <div className="font-medium text-slate-700">{r.referrer_name}</div>
                          <div className="text-xs text-slate-400">{r.referrer_email}</div>
                        </td>
                        <td className="py-2.5 pr-3 text-slate-600">{r.referee_email}</td>
                        <td className="py-2.5 pr-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${pill.cls}`}>{pill.label}</span>
                        </td>
                        <td className="py-2.5 pr-3 text-slate-600">{r.plan_label || '—'}</td>
                        <td className="py-2.5 pr-3 text-right font-semibold text-slate-800">{r.converted ? INR(r.amount) : '—'}</td>
                        <td className="py-2.5 pr-3 text-center text-amber-600">{r.points_awarded || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-4 text-sm">
              <span className="text-slate-500">
                {conv.total} referrals · <span className="font-medium text-slate-700">{INR(conv.sum_amount)}</span> attributed
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="referral-prev-page">Prev</Button>
                <span className="text-slate-500">{page} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="referral-next-page">Next</Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
